// LooksViewReports.js

import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';

import { LooksReportsContext }  from '../contexts/LooksReportsContext';
import { LooksPostsContext }    from '../contexts/LooksPostsContext';

import {
  getPostById,
  getUserById,
  markPostAsReviewed,
  markReportAsReviewed,
  markReportAsDeleted
} from '../utils/dbUtilities';

import './ViewReports.css';

const LooksViewReports = () => {
  const { reports } = useContext(LooksReportsContext);
  const { updateReportedStatus, deletePostById } = useContext(LooksPostsContext);

  const [details, setDetails] = useState([]);

  /* ─────────────  build enriched list  ───────────── */
  useEffect(() => {
    const hydrate = async () => {
      const rows = await Promise.all(
        reports.map(async (r) => {
          const post = await getPostById(r.postId, 'looksPosts');
          const user = await getUserById(r.userId);

          return {
            ...r,
            postTitle:  post ? post.title || post.userName || '(untitled)' : 'Post not found',
            username:   user ? user.username || user.displayName || 'Unknown' : 'Unknown',
            postLink:   post ? `/looksposts/${r.postId}` : null,
            reviewed:   !!r.reviewed,
            postDeleted: !!r.postDeleted,
            ts:         r.timestamp ? new Date(r.timestamp).getTime() : null
          };
        })
      );

      rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setDetails(rows);
    };

    hydrate();
  }, [reports]);

  /* ─────────────  actions  ───────────── */
  const markReviewed = async (postId, reportId) => {
    try {
      await markPostAsReviewed(postId, 'looksPosts');
      await updateReportedStatus(postId, false);          // clear red flag
      await markReportAsReviewed(reportId, 'looksReports');

      setDetails((d) =>
        d.map((row) => (row.id === reportId ? { ...row, reviewed: true } : row))
      );
    } catch (err) {
      console.error('Error marking reviewed', err);
    }
  };

  const deletePost = async (postId, reportId) => {
    try {
      if (!window.confirm('Delete this post?')) return;

      await deletePostById(postId);
      await markReportAsDeleted(reportId, 'looksReports');

      setDetails((d) =>
        d.map((row) =>
          row.id === reportId ? { ...row, postDeleted: true } : row
        )
      );
    } catch (err) {
      console.error('Error deleting post', err);
    }
  };

  /* ─────────────  helpers  ───────────── */
  const fmt  = (t)  => moment(t).tz('America/New_York').format('YYYY-MM-DD HH:mm:ss');
  const safe = (txt)=> (typeof txt === 'string' ? txt : 'Invalid');

  /* ─────────────  render  ───────────── */
  return (
    <div className="view-reports-container">
      <h2>View Reports</h2>

      <div className="reports-list">
        {details.map((r) => (
          <div key={r.id} className="report-item">
            <p>Type: {r.type}</p>
            <p>ID: {r.id}</p>
            <p>Reported&nbsp;By: {r.username}</p>
            <p>Reported&nbsp;At: {fmt(r.ts)}</p>
            <p>Reason: {safe(r.reason)}</p>
            <p>Details: {safe(r.text)}</p>

            {r.postLink && (
              <p>
                Post:&nbsp;
                <Link to={r.postLink} className="view-post-link">
                  {r.postTitle}
                </Link>
              </p>
            )}

            <div className="report-action">
              {r.postDeleted ? (
                <p>Post Deleted</p>
              ) : r.reviewed ? (
                <p>Marked as Reviewed</p>
              ) : (
                <>
                  <button onClick={() => markReviewed(r.postId, r.id)}>
                    Mark as Reviewed
                  </button>
                  <button onClick={() => deletePost(r.postId, r.id)}>
                    Delete Post
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LooksViewReports;
